import type { OidcConfiguration } from '@axa-fr/react-oidc';
import {
  OidcProvider,
  OidcSecure,
  OidcUserStatus,
  useOidc,
  useOidcAccessToken,
  useOidcIdToken,
  useOidcUser,
} from '@axa-fr/react-oidc';
import { browserLogger } from '@newfront-insurance/dd-rum';
import type { DecodedIdentityJwt, DecodedJwt } from '@newfront-insurance/next-auth-api-types';
import type { Provider } from '@newfront-insurance/react-provision';
import { createProvider, useProvider } from '@newfront-insurance/react-provision';
import { useRouter } from 'next/router';
import { WebStorageStateStore, type User } from 'oidc-client-ts';
import type { JSX, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { AuthProvider, useAuth } from 'react-oidc-context';

import { SessionTimeoutErrorBoundary } from '../boundaries/session-timeout-error-boundary';
import type { KeycloakAuthClient } from '../clients/keycloak';
import { getKeycloakAuthClient } from '../clients/keycloak';
import { SessionStoragePersistor } from '../clients/persistors/session-storage';
import { IDPHint } from '../constants';
import { isTokenExpired, parseTokenPayload } from '../internal/helpers/jwt';
import type {
  Account,
  AuthApiConfig,
  AuthClient,
  AuthClientState,
  AuthProviderContext,
  AuthState,
  AuthSwapProviderContext,
  SwapContext,
  UserProfile,
} from '../types';

export enum AuthProviderType {
  OIDC_CLIENT = 'OIDC_CLIENT',
  OIDC_CONTEXT = 'OIDC_CONTEXT',
  KEYCLOAK_JS = 'KEYCLOAK_JS',
}

interface Props {
  idpHint?: IDPHint;
  redirectUri?: string;
  authApiConfig: AuthApiConfig;
  onSessionLost?: () => void;
}
interface SwapProviderOptions {
  authProvider: Provider<AuthProviderContext, AuthProviderProps>;
  clientId: string;
  isEnabled?: boolean;
}

/**
 * Subscribe to the auth state on an AuthClient
 * @param client AuthClient
 * @returns AuthState
 */
export function useAuthState(client: AuthClient): AuthState {
  const [authState, setAuthState] = useState<AuthState>(() => client.getAuthState());

  useEffect(() => {
    const unsub = client.subscribe((newState) => setAuthState(newState));
    return () => {
      unsub();
    };
  }, [client]);

  return authState;
}

/**
 * Get the user profile from the auth state on the auth client.
 * @param tokens The state from the AuthClient
 * @returns UserProfile
 */
export function useUserProfile(tokens: Pick<AuthClientState, 'idToken' | 'token'>): UserProfile | undefined {
  const { idToken, token } = tokens;

  return useMemo<UserProfile | undefined>(() => {
    if (!token || !idToken) {
      return undefined;
    }
    const parsedToken = parseTokenPayload<DecodedJwt & { newfront_accounts: Account[]; accountUuids?: string[] }>(
      token,
    );
    const parsedIdToken = parseTokenPayload<DecodedIdentityJwt>(idToken);
    const scopes = parsedToken.scope?.split(' ') ?? [];

    return {
      uuid: parsedIdToken.userId,
      email: parsedIdToken.email,
      profilePic: parsedIdToken.profilePic,
      roles: scopes,
      scopes,
      firstName: parsedIdToken.given_name,
      lastName: parsedIdToken.family_name,
      accounts: parsedToken.newfront_accounts ?? [],
      accountUuid: parsedToken.accountUuids?.[0],
    };
  }, [idToken, token]);
}

/**
 * Login using the client.
 * @param client AuthClient
 */
export function useAuthClientLogin(client: AuthClient): void {
  const [error, setError] = useState<Error>();
  const { isReady, isLoggedIn } = useAuthState(client);

  // Start the auth client
  useEffect(() => {
    if (!isReady) {
      client.start().catch((e) => {
        setError(e);
      });
    }
  }, [isReady, client]);

  // Login when ready
  useEffect(() => {
    if (isReady && !isLoggedIn) {
      client.login().catch((e) => {
        setError(e);
      });
    }
  }, [isLoggedIn, isReady, client]);

  if (error) {
    throw error;
  }
}

/**
 * Returns a function that can be used to get an updated auth token that can be used
 * to make requests. This should be called before every request.
 * @param client AuthClient
 * @returns
 */
export function useAccessTokenCallback(client: AuthClient): () => Promise<string | undefined> {
  return useCallback(async () => {
    const tokens = await client.updateTokens();

    return tokens?.token;
  }, [client]);
}

/**
 * Returns an async callback that will swap the current token for a another token
 * for a different client id.
 */
function useSwapTokenProvider(
  authClient: AuthClient,
  clientId: string,
  isEnabled = true,
  initialSwapContext: SwapContext | undefined = undefined,
): AuthSwapProviderContext {
  const authState = useAuthState(authClient);
  const [token, setToken] = useState<string>();
  const [scopes, setScopes] = useState<string[]>();
  const [swapContext, setSwapContext] = useState<SwapContext | undefined>(initialSwapContext);
  const userDetails = useUserProfile({ idToken: authState.idToken, token });

  // Use refs to store mutable state that shouldn't trigger rerenders
  const stateRef = useRef({
    token,
    swapContext,
    pendingSwap: undefined as Promise<string | undefined> | undefined,
    isSwapping: false,
  });

  // Keep ref up to date without triggering rerenders
  useEffect(() => {
    stateRef.current.token = token;
    stateRef.current.swapContext = swapContext;
  }, [token, swapContext]);

  // Create a stable request swap token function that always uses latest state
  const requestSwapToken = useCallback(async () => {
    const state = stateRef.current;

    // Check auth state at execution time
    const currentAuthState = authClient.getAuthState();
    if (!currentAuthState.isLoggedIn || !isEnabled || !state.swapContext?.newfrontAccountUuid) {
      browserLogger.info('Skipping token swap - prerequisites not met', {
        isLoggedIn: currentAuthState.isLoggedIn,
        isEnabled,
        hasContext: !!state.swapContext?.newfrontAccountUuid,
      });
      return;
    }

    // If already swapping, wait for the existing swap to complete
    if (state.isSwapping && state.pendingSwap) {
      browserLogger.info('Swap already in progress, waiting for existing swap');
      return state.pendingSwap;
    }

    try {
      state.isSwapping = true;
      const { token: newToken } = await authClient.swap(clientId, state.swapContext);
      setToken(newToken);
      return newToken;
    } catch (error) {
      browserLogger.error('Token swap failed:', { error });
      if (error instanceof Error) {
        if (
          error.message.includes('401') ||
          error.message.includes('403') ||
          error.message.includes('invalid_token') ||
          error.message.includes('Token invalid')
        ) {
          setToken(undefined);
        }
      }
      throw error;
    } finally {
      state.isSwapping = false;
      state.pendingSwap = undefined;
    }
  }, [authClient, clientId, isEnabled]); // Only depend on stable values

  // Create a stable getToken function that always uses latest state
  const getSwappedAccessToken = useCallback(async () => {
    const state = stateRef.current;

    // If there's no context, return undefined immediately
    if (!state.swapContext) {
      return undefined;
    }

    // If there's a pending swap, wait for it
    if (state.pendingSwap) {
      return state.pendingSwap;
    }

    // If we have a valid token, return it
    if (state.token && !isTokenExpired(state.token)) {
      return state.token;
    }

    // Start new swap request
    try {
      state.pendingSwap = requestSwapToken();
      const result = await state.pendingSwap;
      return result;
    } catch (error) {
      state.pendingSwap = undefined;
      throw error;
    }
  }, [requestSwapToken]); // Only depend on requestSwapToken

  // Wrap setSwapContext to start the token swap synchronously
  // This ensures pendingSwap is set before any queries run after re-render
  const setSwapContextAndSwap: typeof setSwapContext = useCallback(
    (newContextOrUpdater) => {
      // Handle both direct value and updater function forms
      const newContext =
        typeof newContextOrUpdater === 'function'
          ? newContextOrUpdater(stateRef.current.swapContext)
          : newContextOrUpdater;

      if (newContext?.newfrontAccountUuid) {
        const currentAuthState = authClient.getAuthState();
        if (currentAuthState.isLoggedIn && isEnabled) {
          // Clear token and start swap BEFORE triggering re-render
          stateRef.current.token = undefined;
          stateRef.current.swapContext = newContext;

          // Start the swap immediately and store the promise
          // This must happen before setSwapContext triggers re-renders
          if (!stateRef.current.isSwapping) {
            stateRef.current.isSwapping = true;
            stateRef.current.pendingSwap = authClient.swap(clientId, newContext).then(
              ({ token: newToken }) => {
                setToken(newToken);
                stateRef.current.isSwapping = false;
                stateRef.current.pendingSwap = undefined;
                return newToken;
              },
              (error) => {
                browserLogger.error('Token swap failed:', { error });
                stateRef.current.isSwapping = false;
                stateRef.current.pendingSwap = undefined;
                if (error instanceof Error) {
                  if (
                    error.message.includes('401') ||
                    error.message.includes('403') ||
                    error.message.includes('invalid_token') ||
                    error.message.includes('Token invalid')
                  ) {
                    setToken(undefined);
                  }
                }
                throw error;
              },
            );
          }

          setScopes(undefined);
          setToken(undefined);
        }
      }
      setSwapContext(newContext);
    },
    [authClient, clientId, isEnabled, setSwapContext],
  );

  // Effect to handle initial swap context (e.g., from URL on page load)
  useEffect(() => {
    if (!swapContext?.newfrontAccountUuid) return;
    // Skip if swap already in progress (started by setSwapContextAndSwap)
    if (stateRef.current.pendingSwap) return;

    const currentAuthState = authClient.getAuthState();
    if (!currentAuthState.isLoggedIn) {
      browserLogger.info('Not logged in, skipping token swap');
      return;
    }

    setScopes(undefined);
    setToken(undefined);

    // Store the pending swap promise so getSwappedAccessToken can await it
    stateRef.current.pendingSwap = requestSwapToken();
  }, [swapContext, authClient, requestSwapToken]);

  return {
    getSwappedAccessToken,
    getToken: getSwappedAccessToken,
    scopes,
    swapContext,
    setSwapContext: setSwapContextAndSwap,
    userDetails,
  };
}

/**
 * Create the KeycloakAuthClient that will be used to manage the JWT token.
 */
function useKeycloakAuthClient(options: Props): KeycloakAuthClient {
  const { authApiConfig, idpHint, redirectUri } = options;
  const { clientId, realm, url } = authApiConfig;

  return useMemo(() => {
    return getKeycloakAuthClient({
      config: {
        clientId,
        realm,
        url,
      },
      initOptions: {
        pkceMethod: 'S256',
      },
      loginOptions: {
        idpHint,
        redirectUri,
      },
      tokenPersistor: new SessionStoragePersistor(`kcToken-${realm}-${clientId}`, window.localStorage),
    });
  }, [clientId, idpHint, realm, redirectUri, url]);
}

export interface AuthProviderProps {
  providerType?: AuthProviderType;
  children?: ReactNode;
}

export const createAuthProvider = (props: Props): Provider<AuthProviderContext, AuthProviderProps> => {
  const { authApiConfig } = props;
  const { clientId, realm } = authApiConfig;

  return createProvider<AuthProviderContext, AuthProviderProps>(
    ({ providerType }) => {
      if (providerType === AuthProviderType.OIDC_CLIENT) {
        return useOIDCAuthProvider();
      }

      if (providerType === AuthProviderType.OIDC_CONTEXT) {
        return useOIDCAuthContext();
      }

      const client = useKeycloakAuthClient(props);
      const authState = useAuthState(client);
      const userDetails = useUserProfile(authState);
      const logout = useCallback(async (redirectUrl: string) => client.logout(redirectUrl), [client]);
      const getAccessTokenCallback = useAccessTokenCallback(client);

      return {
        getAccessTokenCallback,
        getToken: getAccessTokenCallback,
        logout,
        userDetails,
        authState,
        client,
      } as AuthProviderContext;
    },
    {
      name: `AuthProvider-${realm}-${clientId}`,
      wrapper: ({ children, providerType }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const router = useRouter();
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const localStateStore = useMemo(() => {
          return new WebStorageStateStore({ store: window.localStorage });
        }, []);
        if (providerType === AuthProviderType.OIDC_CLIENT) {
          const configuration: OidcConfiguration = {
            client_id: authApiConfig.clientId,
            redirect_uri: `${window.location.origin}/#authentication/callback`,
            silent_redirect_uri: `${window.location.origin}/#authentication/silent-callback`,
            monitor_session: true,
            scope: 'openid profile email offline_access',
            authority: `${authApiConfig.url}/realms/${authApiConfig.realm}`,
            refresh_time_before_tokens_expiration_in_second: 30,
            storage: localStorage,
            extras:
              props.idpHint === IDPHint.GSUITE
                ? {
                    kc_idp_hint: 'gsuite',
                  }
                : {},
          };

          const withCustomHistory = () => {
            return {
              replaceState: (url: string) => {
                router
                  .replace({
                    pathname: url,
                  })
                  .then(() => {
                    window.dispatchEvent(new Event('popstate'));
                  });
              },
            };
          };

          return (
            <OidcProvider
              onEvent={(_config, eventName, data) => {
                if (eventName.includes('error')) {
                  console.error('[Newfront Auth Error]', eventName, data);
                }
              }}
              withCustomHistory={withCustomHistory}
              configuration={configuration}
              onSessionLost={() => {
                console.error('[Newfront Auth Error]', 'Session lost');
              }}
              authenticatingComponent={() => null}
              authenticatingErrorComponent={() => null}
              loadingComponent={() => null}
              callbackSuccessComponent={() => null}
              sessionLostComponent={() => {
                return <SessionTimeoutErrorBoundary />;
              }}
            >
              <OidcSecure>{children}</OidcSecure>
            </OidcProvider>
          );
        }

        if (providerType === AuthProviderType.OIDC_CONTEXT) {
          return (
            <AuthProvider
              authority={`${authApiConfig.url}/realms/${authApiConfig.realm}`}
              client_id={authApiConfig.clientId}
              redirect_uri={`${window.location.href}`}
              automaticSilentRenew
              userStore={localStateStore}
            >
              {children}
            </AuthProvider>
          );
        }

        return children as JSX.Element;
      },
    },
  );
};

export interface AuthSwapProviderOptions {
  initialSwapContext?: SwapContext;
}

export function createAuthSwapProvider(
  options: SwapProviderOptions,
): Provider<AuthSwapProviderContext, AuthSwapProviderOptions> {
  const { authProvider, clientId, isEnabled = true } = options;

  return createProvider((opts: AuthSwapProviderOptions) => {
    const { initialSwapContext } = opts;
    const { client } = useProvider(authProvider);
    return useSwapTokenProvider(client, clientId, isEnabled, initialSwapContext);
  });
}

function getOidcAuthState(isLoading: boolean, isAuthenticated: boolean, user: User | null | undefined): AuthState {
  if (isLoading) {
    return {
      state: 'NOT_READY',
      isReady: false,
      isLoggedIn: false,
      idToken: undefined,
      token: undefined,
    };
  }
  if (!isAuthenticated) {
    return {
      state: 'NOT_LOGGED_IN',
      isReady: true,
      isLoggedIn: false,
      idToken: undefined,
      token: undefined,
    };
  }

  return {
    state: 'LOGGED_IN',
    isReady: true,
    isLoggedIn: true,
    idToken: user?.id_token ?? '',
    token: user?.access_token ?? '',
    error: undefined,
  };
}

export function useOIDCAuthContext(): AuthProviderContext {
  const { user, isAuthenticated, isLoading, signinPopup, signinSilent, signoutRedirect, signinRedirect, removeUser } =
    useAuth();

  // Store latest user in a ref so long‑lived/cached consumers of getToken read the current user/token, not a stale closure
  const userRef = useRef(user);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const client: AuthClient = useMemo(() => {
    return {
      login: async (redirectUri: string) => {
        signinSilent({
          redirect_uri: redirectUri,
          extraQueryParams: { kc_idp_hint: 'gsuite' },
        });
      },
      loginPopup: async (redirectUri: string) => {
        if (!isAuthenticated) {
          // Auth and Refresh Token expired. Must go through a clean login flow.
          // Otherwise, subsequent `login` call will be made with a stale Refresh Token, thus erroring.
          await removeUser();
        }
        signinPopup({
          redirect_uri: redirectUri,
          extraQueryParams: { kc_idp_hint: 'gsuite' },
        });
      },
      loginRedirect: async (redirectUri: string) => {
        signinRedirect({ redirect_uri: redirectUri, extraQueryParams: { kc_idp_hint: 'gsuite' } });
      },
      // Not necessary when using OIDC
      start: async () => {
        return true;
      },
      // Not necessary when using OIDC
      stop: () => {
        return true;
      },
      // Not necessary when using OIDC
      subscribe: () => {
        return () => {
          return true;
        };
      },
      // Not necessary when using OIDC
      updateTokens: async () => {
        const newUser = await signinSilent();

        return {
          idToken: newUser?.id_token ?? '',
          token: newUser?.access_token ?? '',
          error: undefined,
        };
      },
      getAuthState: () => getOidcAuthState(isLoading, isAuthenticated, userRef.current),
      logout: async (_redirectUri: string) => {
        await signoutRedirect();
      },
      swap: async () => {
        return {
          token: '',
          refreshToken: '',
        };
      },
      getDirectGrantLoginToken: async () => {
        return undefined;
      },
      authenticateWithMagicLink: async () => {
        return '';
      },
      requestMagicLink: async () => {
        return false;
      },
    };
  }, [signinPopup, signinSilent, signoutRedirect, isAuthenticated, signinRedirect, user, isLoading]);

  return {
    authState: getOidcAuthState(isLoading, isAuthenticated, userRef.current),
    userDetails: {
      accounts: [],
      email: user?.profile.email as string,
      firstName: user?.profile.given_name as string,
      lastName: user?.profile.family_name as string,
      roles: user?.profile.roles as string[],
      scopes: user?.scope?.split(' ') ?? [],
      uuid: user?.profile.userId as string,
      profilePic: user?.profile.picture as string,
    },
    getAccessTokenCallback: async () => userRef.current?.access_token ?? '',
    client,
    getToken: async () => {
      return userRef.current?.access_token;
    },
    logout: async () => {
      await signoutRedirect();
    },
  };
}

const useOIDCAuthProvider = (): AuthProviderContext => {
  const { accessToken, accessTokenPayload } = useOidcAccessToken();
  const { idToken, idTokenPayload } = useOidcIdToken();
  const { logout, login } = useOidc();
  const authState = useOIDCAuthStateFromUser();

  return {
    getAccessTokenCallback: () => accessToken,
    getToken: () => accessToken,
    userDetails: {
      accounts: [],
      email: idTokenPayload.email as string,
      firstName: idTokenPayload.given_name as string,
      lastName: idTokenPayload.family_name as string,
      roles: accessTokenPayload?.scope?.split(' ') ?? [],
      scopes: accessTokenPayload?.scope?.split(' ') ?? [],
      uuid: accessTokenPayload.userId,
      profilePic: idTokenPayload?.profilePic,
    },
    authState,
    logout: async (redirectUri: string) => {
      await logout(redirectUri);
    },
    client: {
      login: async () => {
        await login();
      },
      // Not necessary when using OIDC
      start: async () => {
        return true;
      },
      // Not necessary when using OIDC
      stop: () => {
        return true;
      },
      // Not necessary when using OIDC
      subscribe: () => {
        return () => {
          return true;
        };
      },
      // Not necessary when using OIDC
      updateTokens: async () => ({
        idToken: '',
        token: '',
      }),
      getAuthState: () => ({
        state: 'LOGGED_IN',
        isLoggedIn: true,
        isReady: true,
        idToken,
        token: accessToken,
      }),

      logout: async (redirectUri: string) => {
        await logout(redirectUri);
      },
      // TODO: to be implemented
      swap: async () => {
        return {
          token: '',
          refreshToken: '',
        };
      },
      getDirectGrantLoginToken: async () => {
        return undefined;
      },
      authenticateWithMagicLink: async () => {
        return '';
      },
      requestMagicLink: async () => {
        return false;
      },
    },
  };
};

export class SessionTimeoutError extends Error {}

function useOIDCAuthStateFromUser(): AuthState {
  const { oidcUserLoadingState } = useOidcUser();
  const { accessToken } = useOidcAccessToken();
  const { idToken } = useOidcIdToken();

  if (oidcUserLoadingState === OidcUserStatus.Loaded) {
    return {
      state: 'LOGGED_IN',
      idToken,
      isLoggedIn: true,
      token: accessToken,
      isReady: true,
    };
  }

  return {
    state: 'NOT_READY',
    idToken,
    isLoggedIn: false,
    isReady: false,
    token: accessToken,
  };
}
